/**
 * REGRIND_RECYCLE_PRD §FR-B4 — Grinding Log page.
 *
 * UI parity with RM Production (Feb 2026 ask): title block + global Branch
 * picker in the top-right, three tabs (Grinding / Grinding Log / Reports).
 *
 *   • Grinding (entry)    — submit a new grinding event, +kg to regrind stock.
 *   • Grinding Log        — paginated history with date filter.
 *   • Reports             — client-side aggregates over the log: kg by
 *                           regrind item and by source type for the period.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import useBranchStore from "@/store/branchStore";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Recycle, Plus, FileText, BarChart3, Save, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SOURCE_TYPES = ["SPRUE_RUNNER", "REJECT", "PURGE", "OTHER"];

export default function GrindingLog() {
  const { selectedBranch: globalBranch, setSelectedBranch: setGlobalBranch } = useBranchStore();
  const [selectedBranch, setSelectedBranch] = useState(globalBranch || "");
  useEffect(() => {
    if (globalBranch && globalBranch !== selectedBranch) setSelectedBranch(globalBranch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalBranch]);

  const [activeTab, setActiveTab] = useState("grind");
  const [branches, setBranches] = useState([]);
  const [regrindItems, setRegrindItems] = useState([]);

  // Entry form state
  const [form, setForm] = useState({
    regrind_rm_id: "",
    kg_produced: "",
    source_type: "SPRUE_RUNNER",
    source_rm_id: "",
    notes: "",
    grind_date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  // List state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [listLoading, setListLoading] = useState(false);

  // Reports state — same period model as RM Production (last 30 days default)
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const daysAgoISO = (n) => {
    const d = new Date(); d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const [summaryPeriod, setSummaryPeriod] = useState({
    start: daysAgoISO(30), end: todayISO(),
  });
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // ---- Bootstrap branches + regrind items ----
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/branches`);
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setBranches(items);
        if (!selectedBranch && items.length === 1) {
          const name = items[0].name || items[0].code;
          setSelectedBranch(name);
          if (setGlobalBranch) setGlobalBranch(name);
        }
      } catch { /* user can still pick */ }
      try {
        const r = await axios.get(`${API}/raw-materials`, {
          params: { category: "REGRIND", limit: 5000 },
        });
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        // Defensive client-side filter (the backend ?category= param is ignored).
        const regrindOnly = items.filter(x => (x.category || "").toUpperCase() === "REGRIND");
        regrindOnly.sort((a, b) => (a.rm_id || "").localeCompare(b.rm_id || ""));
        setRegrindItems(regrindOnly);
      } catch { /* empty dropdown */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- List fetch ----
  const fetchList = async (overrides = {}) => {
    if (!selectedBranch) { setRows([]); setTotal(0); return; }
    setListLoading(true);
    try {
      const params = {
        branch: selectedBranch,
        page: overrides.page || page,
        page_size: pageSize,
      };
      const s = overrides.start ?? filterStart;
      const e = overrides.end ?? filterEnd;
      if (s) params.start_date = s;
      if (e) params.end_date = e;
      const r = await axios.get(`${API}/grinding/log`, { params });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load grinding log");
    } finally {
      setListLoading(false);
    }
  };
  useEffect(() => {
    if (activeTab === "log" && selectedBranch) fetchList({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, selectedBranch]);

  // ---- Reports fetch (single big page then aggregate client-side) ----
  const fetchReports = async () => {
    if (!selectedBranch || !summaryPeriod.start || !summaryPeriod.end) return;
    setSummaryLoading(true);
    try {
      const r = await axios.get(`${API}/grinding/log`, {
        params: {
          branch: selectedBranch,
          start_date: summaryPeriod.start,
          end_date: summaryPeriod.end,
          page_size: 200,
          page: 1,
        },
      });
      setSummaryRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load report data");
    } finally {
      setSummaryLoading(false);
    }
  };
  useEffect(() => {
    if (activeTab === "reports" && selectedBranch) fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBranch, summaryPeriod.start, summaryPeriod.end]);

  // Aggregations for the Reports tab — by regrind item + by source type
  const reportAggregates = useMemo(() => {
    const byRegrind = {};
    const bySource = {};
    let totalKg = 0;
    for (const r of summaryRows) {
      const kg = Number(r.kg_produced) || 0;
      totalKg += kg;
      byRegrind[r.regrind_rm_id] = byRegrind[r.regrind_rm_id] || {
        rm_id: r.regrind_rm_id, name: r.regrind_name, kg: 0, count: 0,
      };
      byRegrind[r.regrind_rm_id].kg += kg;
      byRegrind[r.regrind_rm_id].count += 1;
      bySource[r.source_type] = (bySource[r.source_type] || 0) + kg;
    }
    return {
      totalKg, entries: summaryRows.length,
      byRegrind: Object.values(byRegrind).sort((a, b) => b.kg - a.kg),
      bySource: Object.entries(bySource)
        .map(([k, v]) => ({ source_type: k, kg: v }))
        .sort((a, b) => b.kg - a.kg),
    };
  }, [summaryRows]);

  // ---- Save handler ----
  const handleSave = async () => {
    if (!selectedBranch) return toast.error("Select a branch in the top-right first");
    if (!form.regrind_rm_id) return toast.error("Pick a regrind item");
    const kg = parseFloat(form.kg_produced);
    if (!(kg > 0)) return toast.error("kg produced must be > 0");

    setSaving(true);
    try {
      await axios.post(`${API}/grinding/log`, {
        branch: selectedBranch,
        regrind_rm_id: form.regrind_rm_id,
        kg_produced: kg,
        source_type: form.source_type,
        source_rm_id: form.source_rm_id || null,
        notes: form.notes || null,
        grind_date: form.grind_date,
      });
      toast.success("Grinding logged — regrind stock updated");
      setForm(prev => ({ ...prev, kg_produced: "", source_rm_id: "", notes: "" }));
      // If user is on log/reports, refresh those views silently.
      if (activeTab === "log") fetchList({ page: 1 });
      if (activeTab === "reports") fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save grinding log");
    } finally {
      setSaving(false);
    }
  };

  const selectedRegrind = useMemo(
    () => regrindItems.find(r => r.rm_id === form.regrind_rm_id) || null,
    [regrindItems, form.regrind_rm_id],
  );
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  // ============================================================
  return (
    <div className="p-6 space-y-6" data-testid="grinding-log-page">
      {/* ---- Header (mirrors RM Production layout) ---- */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Recycle className="w-6 h-6 text-emerald-600" />
            Grinding Inward
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Bank ground scrap into regrind stock and track consumption.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-[220px]">
            <Label className="text-xs text-gray-500">Branch</Label>
            <Select
              value={selectedBranch || "select"}
              onValueChange={(v) => {
                setSelectedBranch(v);
                if (setGlobalBranch) setGlobalBranch(v);
              }}
            >
              <SelectTrigger data-testid="branch-select">
                <SelectValue placeholder="Select Branch" />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => {
                  const name = b.name || b.code;
                  return (
                    <SelectItem key={b.id || name} value={name}
                                data-testid={`branch-option-${name.replace(/\s+/g, "-")}`}>
                      {name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* ---- Tabs ---- */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-[500px] grid-cols-3">
          <TabsTrigger value="grind" data-testid="grind-tab">
            <Plus className="w-4 h-4 mr-2" />
            Grinding
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="grind-log-tab">
            <FileText className="w-4 h-4 mr-2" />
            Grinding Log
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="grind-reports-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
        </TabsList>

        {/* ============ Grinding (entry) tab ============ */}
        <TabsContent value="grind" className="space-y-6">
          {!selectedBranch ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Recycle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Please select a branch to record grinding</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: entry form */}
              <div className="lg:col-span-2">
                <Card data-testid="grinding-log-form-card">
                  <CardHeader>
                    <CardTitle className="text-lg">New Grinding Entry</CardTitle>
                    <CardDescription>
                      Record ground scrap and bank it into regrind stock for {selectedBranch}.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={form.grind_date}
                             onChange={(e) => setForm(p => ({ ...p, grind_date: e.target.value }))}
                             data-testid="grinding-date-input" />
                    </div>
                    <div>
                      <Label>Regrind Item <span className="text-red-600">*</span></Label>
                      <Select value={form.regrind_rm_id}
                              onValueChange={(v) => setForm(p => ({ ...p, regrind_rm_id: v }))}>
                        <SelectTrigger data-testid="grinding-regrind-select">
                          <SelectValue placeholder="Pick regrind RM (grade + colour)" />
                        </SelectTrigger>
                        <SelectContent>
                          {regrindItems.length === 0 ? (
                            <SelectItem value="__none__" disabled>
                              No regrind items — create one in RM Repository first
                            </SelectItem>
                          ) : regrindItems.map(r => (
                            <SelectItem key={r.rm_id} value={r.rm_id}
                                        data-testid={`grinding-regrind-option-${r.rm_id}`}>
                              {r.rm_id} — {r.description || r?.category_data?.colour || ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedRegrind && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Parent: {(selectedRegrind?.parent_grades || []).join(", ") || "—"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Kg Produced <span className="text-red-600">*</span></Label>
                      <Input type="number" step="0.01" min="0.01"
                             value={form.kg_produced}
                             onChange={(e) => setForm(p => ({ ...p, kg_produced: e.target.value }))}
                             placeholder="e.g. 42.5"
                             data-testid="grinding-kg-input" />
                    </div>
                    <div>
                      <Label>Source Type <span className="text-red-600">*</span></Label>
                      <Select value={form.source_type}
                              onValueChange={(v) => setForm(p => ({ ...p, source_type: v }))}>
                        <SelectTrigger data-testid="grinding-source-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SOURCE_TYPES.map(t => (
                            <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Source Part RM ID (optional)</Label>
                      <Input value={form.source_rm_id}
                             onChange={(e) => setForm(p => ({ ...p, source_rm_id: e.target.value }))}
                             placeholder="e.g. INP_654"
                             data-testid="grinding-source-rm-input" />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Notes (optional)</Label>
                      <Textarea value={form.notes}
                                onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                                placeholder="Anything the production team should know about this grind…"
                                rows={2}
                                data-testid="grinding-notes-input" />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                      <Button onClick={handleSave} disabled={saving}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid="grinding-save-btn">
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? "Saving…" : "Log Grinding"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right: live "Banking into" panel — mirrors RM Production Details */}
              <div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Grinding Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                        Banking Into
                      </p>
                      <p className="text-base font-semibold text-emerald-900">{selectedBranch}</p>
                    </div>
                    {selectedRegrind ? (
                      <div className="space-y-2 text-sm">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-500">Regrind RM</p>
                          <p className="font-mono text-xs">{selectedRegrind.rm_id}</p>
                          <p>{selectedRegrind.description}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-500">Parent POLY</p>
                          <p className="font-mono text-xs">
                            {(selectedRegrind?.parent_grades || []).join(", ") || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-500">Source</p>
                          <Badge variant="outline">{form.source_type}</Badge>
                        </div>
                        {form.kg_produced && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-500">Will add</p>
                            <p className="text-xl font-semibold text-emerald-700">
                              + {parseFloat(form.kg_produced || 0).toLocaleString()} KG
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 py-8">
                        <Recycle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">Pick a regrind item to log</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============ Grinding Log (history) tab ============ */}
        <TabsContent value="log" className="space-y-4">
          <Card data-testid="grinding-log-list-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Recent Entries ({total})</CardTitle>
                <CardDescription>
                  Grinding events recorded for {selectedBranch || "your branches"}.
                </CardDescription>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" className="h-8" value={filterStart}
                         onChange={(e) => setFilterStart(e.target.value)}
                         data-testid="grinding-filter-start" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" className="h-8" value={filterEnd}
                         onChange={(e) => setFilterEnd(e.target.value)}
                         data-testid="grinding-filter-end" />
                </div>
                <Button variant="outline" size="sm" className="h-8"
                        onClick={() => { setPage(1); fetchList({ page: 1 }); }}
                        data-testid="grinding-filter-apply">
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Apply
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Grind Code</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Regrind</TableHead>
                      <TableHead className="text-right">Kg</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listLoading ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No grinding entries yet.</TableCell></TableRow>
                    ) : rows.map(r => (
                      <TableRow key={r.id} data-testid={`grinding-row-${r.grind_code}`}>
                        <TableCell className="font-mono text-xs">{r.grind_code}</TableCell>
                        <TableCell className="text-xs">{r.grind_date}</TableCell>
                        <TableCell className="text-xs">{r.branch}</TableCell>
                        <TableCell className="text-xs">
                          <div className="font-mono">{r.regrind_rm_id}</div>
                          <div className="text-muted-foreground">{r.regrind_name}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{r.kg_produced}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline">{r.source_type}</Badge>
                          {r.source_rm_id && (
                            <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                              {r.source_rm_id}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{r.ground_by_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {lastPage > 1 && (
                <div className="flex items-center justify-end gap-2 mt-3">
                  <Button variant="outline" size="sm" disabled={page <= 1}
                          onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <span className="text-xs text-muted-foreground">
                    Page {page} of {lastPage}
                  </span>
                  <Button variant="outline" size="sm" disabled={page >= lastPage}
                          onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ Reports tab ============ */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Grinding Report</CardTitle>
                <CardDescription>
                  Period totals for {selectedBranch || "your branches"} (max 200 entries).
                </CardDescription>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" className="h-8" value={summaryPeriod.start}
                         onChange={(e) => setSummaryPeriod(p => ({ ...p, start: e.target.value }))}
                         data-testid="grinding-reports-start" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" className="h-8" value={summaryPeriod.end}
                         onChange={(e) => setSummaryPeriod(p => ({ ...p, end: e.target.value }))}
                         data-testid="grinding-reports-end" />
                </div>
                <Button variant="outline" size="sm" className="h-8"
                        onClick={fetchReports} disabled={summaryLoading}
                        data-testid="grinding-reports-refresh">
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Top stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border bg-emerald-50/50 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Total Banked</p>
                  <p className="text-2xl font-bold text-emerald-900">
                    {reportAggregates.totalKg.toLocaleString(undefined, { maximumFractionDigits: 1 })} KG
                  </p>
                </div>
                <div className="rounded-lg border bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Entries</p>
                  <p className="text-2xl font-bold">{reportAggregates.entries}</p>
                </div>
                <div className="rounded-lg border bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Distinct Regrinds</p>
                  <p className="text-2xl font-bold">{reportAggregates.byRegrind.length}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* By regrind item */}
                <div className="border rounded-md">
                  <div className="px-3 py-2 border-b bg-gray-50">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">By Regrind Item</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>RM ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="text-right">Entries</TableHead>
                        <TableHead className="text-right">Kg</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportAggregates.byRegrind.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                          {summaryLoading ? "Loading…" : "No data for this period."}
                        </TableCell></TableRow>
                      ) : reportAggregates.byRegrind.map(row => (
                        <TableRow key={row.rm_id}>
                          <TableCell className="font-mono text-xs">{row.rm_id}</TableCell>
                          <TableCell className="text-xs">{row.name}</TableCell>
                          <TableCell className="text-right text-xs">{row.count}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            {row.kg.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* By source type */}
                <div className="border rounded-md">
                  <div className="px-3 py-2 border-b bg-gray-50">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">By Source Type</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Kg</TableHead>
                        <TableHead className="text-right">% Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportAggregates.bySource.length === 0 ? (
                        <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                          {summaryLoading ? "Loading…" : "No data for this period."}
                        </TableCell></TableRow>
                      ) : reportAggregates.bySource.map(row => {
                        const pct = reportAggregates.totalKg > 0
                          ? (row.kg / reportAggregates.totalKg) * 100 : 0;
                        return (
                          <TableRow key={row.source_type}>
                            <TableCell><Badge variant="outline">{row.source_type}</Badge></TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              {row.kg.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">{pct.toFixed(1)}%</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
